'use client';

import { BaseButton, cn, Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, Popover, PopoverContent, PopoverTrigger } from '@shared/components/shadcn';
import { ChevronDown, Spinner, Success } from '@shared/icons';
import { useState } from 'react';
import { FieldInfo } from '../FieldInfo/FieldInfo';
import { useAutocompleteScroll } from './hooks/useAutocompleteScroll';
import { useAutocompleteSearch } from './hooks/useAutocompleteSearch';
import { useAutocompleteValue } from './hooks/useAutocompleteValue';
import type { AutoCompleteProps } from './types';

// Define classes using the conditional approach with dark mode support
const classes = {
  trigger: `w-full data-[size=default]:h-12.5 py-2 px-4 shadow-none border-border !paragraph
              focus-visible:border-border-active focus-visible:outline-none focus-visible:ring-0
              data-[state=open]:border-border-active
              data-[state=open]:[&_svg]:rotate-180 [&_svg]:opacity-100 [&_svg]:transition-transform`,
  content: 'z-51 min-w-60 overflow-y-auto border-none bg-container-base [box-shadow:0px_2px_2px_0px_rgba(0,0,0,0.04),0px_4px_24px_0px_rgba(0,0,0,0.16)]',
  commandInput: 'h-1 border-none',
  commandList: 'bg-container max-h-50 flex gap-2.5 flex-col',
  loadingContainer: 'flex items-center justify-center py-6 bg-container-base',
  emptyMessage: 'bg-container-base p-3 paragraph-s',
  commandGroup: 'bg-container border-none',
  commandItem: 'first:mt-0 mt-1 py-1.5 px-2 !rounded-none justify-between paragraph-explanatory hover:bg-container data-[selected=true]:bg-container',
  loaderIcon: 'animate-spin text-muted-foreground',
};

function AutoComplete({
  id,
  disabled,
  // Controlled props
  value: controlledValue,
  onValueChange,
  // Legacy props
  initialValue = '',
  initialOpen = false,
  fixedItems = [],
  onChange,
  placeholder = 'Select item...',
  emptyMessage = 'No item found.',
  searchPlaceholder,
  className,
  searchApi,
  debounceMs = 300,
  freeSolo = false,
  fixedGroupName = '',
  apiGroupName = '',
  showGroups = true,
  label = 'Autocomplete test',
  displayFormatter,
  filter,
  triggerProps,
  allowDeselectOnReselect = false,
}: AutoCompleteProps) {
  const [open, setOpen] = useState(initialOpen);

  // Use the search hook
  const { inputValue, apiItems, loading, error, debouncedSearch, handleSearchChange, allItems } = useAutocompleteSearch({
    searchApi,
    debounceMs,
    fixedItems,
    freeSolo,
    onValueChange,
    onChange,
    open,
  });

  // Use the scroll hook with inputValue to reset scroll when search changes
  const { setSelectedItemRef, setCommandListRef } = useAutocompleteScroll(open, inputValue);

  // Use the value hook
  const { value, displayValue, handleSelect } = useAutocompleteValue({
    controlledValue,
    initialValue,
    onValueChange,
    onChange,
    fixedItems,
    apiItems,
    placeholder,
    freeSolo,
    allowDeselectOnReselect,
  });

  // Close popover when an item is selected
  const handleSelectAndClose = (value: string) => {
    handleSelect(value);
    setOpen(false);
  };

  return (
    <>
      <div className="relative" data-testid="autocomplete">
        <FieldInfo label={label} htmlFor={id} />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild className="h-12.5">
            <BaseButton
              id={id}
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className={cn(classes.trigger, className)}
              disabled={disabled}
              data-testid="autocomplete-trigger"
              {...triggerProps}
            >
              <span className={`text-foreground truncate text-left w-full ${value ? 'opacity-100' : 'opacity-60'}`}>
                {displayFormatter && value ? displayFormatter(value, displayValue) : displayValue}
              </span>
              <ChevronDown size="extra-small" className="group-data-[state=open]:rotate-180 mr-1" />
            </BaseButton>
          </PopoverTrigger>
          <PopoverContent
            className={cn('p-0 overflow-hidden border-none w-[var(--radix-popover-trigger-width)]', classes.content)}
            align="start"
            side="bottom"
            sideOffset={5}
            avoidCollisions={false}
            onWheel={event => event.stopPropagation()}
          >
            <Command className="bg-container-base min-w-60" shouldFilter={true} filter={filter}>
              <CommandInput
                placeholder={searchPlaceholder || `Search ${placeholder.toLowerCase()}...`}
                value={inputValue}
                onValueChange={handleSearchChange}
                className={classes.commandInput}
                data-testid="autocomplete-input"
              />
              <CommandList ref={setCommandListRef} className={classes.commandList} data-testid="autocomplete-list">
                <CommandEmpty className={classes.emptyMessage}>{emptyMessage}</CommandEmpty>
                {loading && (
                  <div className={classes.loadingContainer} data-testid="loading-indicator">
                    <Spinner size="tiny" className={classes.loaderIcon} />
                  </div>
                )}
                {!loading && error && (
                  <div className="bg-red-50 text-error-foreground p-3 paragraph-explanatory rounded-md m-2" data-testid="error-message">
                    <p>Failed to load items. Please try again.</p>
                  </div>
                )}
                {/* Fixed items group */}
                {fixedItems.length > 0 && (
                  <CommandGroup heading={showGroups ? fixedGroupName : undefined} className={classes.commandGroup}>
                    {fixedItems.map((item, index) => (
                      <CommandItem
                        key={`fixed-${item.value}-${index}`}
                        value={item.value}
                        onSelect={handleSelectAndClose}
                        className={cn(classes.commandItem, value === item.value && 'bg-container')}
                        data-testid={`autocomplete-item-${item.value}`}
                        ref={value === item.value ? setSelectedItemRef : undefined}
                      >
                        {item.label}
                        <Success size="extra-small" className={value === item.value ? 'opacity-100' : 'opacity-0'} />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {/* API items group */}
                {apiItems.length > 0 && (
                  <CommandGroup heading={showGroups && fixedItems.length > 0 ? apiGroupName : undefined} className={classes.commandGroup}>
                    {apiItems.map((item, index) => (
                      <CommandItem
                        key={`api-${item.value}-${index}`}
                        value={item.value}
                        onSelect={handleSelectAndClose}
                        className={classes.commandItem}
                        data-testid={`autocomplete-item-${item.value}`}
                        ref={value === item.value ? setSelectedItemRef : undefined}
                      >
                        {item.label}
                        <Success size="extra-small" className={value === item.value ? 'opacity-100' : 'opacity-0'} />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {/* Free solo option */}
                {freeSolo && debouncedSearch && allItems.length === 0 && !loading && (
                  <CommandGroup className={classes.commandGroup}>
                    <CommandItem value={debouncedSearch} onSelect={handleSelectAndClose} className={classes.commandItem} data-testid={`autocomplete-item-freesolo`}>
                      {debouncedSearch}
                      <Success size="extra-small" className={value === debouncedSearch ? 'opacity-100' : 'opacity-0'} />
                    </CommandItem>
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </>
  );
}

export { AutoComplete };
